import { remoteStorageApi } from '@mathflat/domain/RemoteStorage'
import type { GradeElementaryMiddle } from '@mathflat/domain/SchoolSystem'
import { makeAutoObservable } from 'mobx'

import { EMPTY_STRING_ID } from '@/constant/common'
import type { DashboardRemoteStorageData } from '@/page/Home/Dashboard/_service/Dashboard.service'
import {
  AchievementAnalysisService,
  type AnalysisAchievementExternal,
  type AnalysisAchievementExternalV1,
  type AnalysisAchievementExternalV2,
  type AnalysisAchievementExternalV3,
} from '@/page/Lesson/AchievementAnalysis/_service/AchievementAnalysis.service'
import type { LearningStatisticsSortedType } from '@/page/Lesson/LearningHistory/_service/StudentHistory.service.ts'
import type { ProblemRequestType } from '@/types/makeWorksheet'

type Notification = {
  startDate: number
  expiredDate: number
  isRead: boolean
}

type RemoteStorageItem = {
  페이지샵_안내사항_다시보지않기: boolean

  PREVIOUS_ACHIEVEMENT_ANALYSIS_BANNER_CLOSED_MS: number | null // Date Time
  visited유형분석: boolean
  PREVIOUS_SELECTED_CURRICULUM_INFO_유형분석: AnalysisAchievementExternal
  visitedWorksheetBoardView: boolean
  visitedWorkbookBoardView: boolean
  learningStatisticsSortedType: LearningStatisticsSortedType
  ONE_CLICK_WORKSHEET_OPTIONS: {
    problemRequestType: ProblemRequestType
    problemCount: '5' | '10' | '15'
  }
  POD_PARENT_PAYMENT_ON_BOARDING_MODAL_DO_NOT_SHOW_AGAIN: boolean
  SCHOOL_DASHBOARD: DashboardRemoteStorageData
  USE_INITIAL_QUICK_MENU_SETTING: boolean
  STARTED_NEW_DESIGN_FLAT: boolean
  VISITED_ACADEMY_INFO_UPDATE_PAGE: boolean
  PUBLIC_WORKBOOK_OPEN_NOTIFICATION: Notification
  ESSAY_WORKSHEET_OPEN_NOTIFICATION: Notification
  KMM_NO_ADVANCED_CURRICULUM_TOOLTIP_DISMISSED: Record<string, boolean>
  MY_DB_LUCKY_WEEK_BANNER_CLOSED: number | null // 닫은 시각 timestamp, null이면 닫은 적 없음
}

class RemoteStorageService implements RemoteStorageItem {
  private relationId: string | null = null

  페이지샵_안내사항_다시보지않기 = false

  visited유형분석 = true
  PREVIOUS_SELECTED_CURRICULUM_INFO_유형분석 = {} as AnalysisAchievementExternalV3
  PREVIOUS_ACHIEVEMENT_ANALYSIS_BANNER_CLOSED_MS: number | null = null
  visitedWorksheetBoardView = false
  visitedWorkbookBoardView = false
  learningStatisticsSortedType = 'STUDENT_NAME_ASC' as LearningStatisticsSortedType
  ONE_CLICK_WORKSHEET_OPTIONS = {} as {
    problemRequestType: 'SIMILAR' | 'WRONG' | 'RANDOM'
    problemCount: '5' | '10' | '15'
  }
  POD_PARENT_PAYMENT_ON_BOARDING_MODAL_DO_NOT_SHOW_AGAIN = false
  SCHOOL_DASHBOARD = {} as DashboardRemoteStorageData
  USE_INITIAL_QUICK_MENU_SETTING = false
  STARTED_NEW_DESIGN_FLAT = false
  VISITED_ACADEMY_INFO_UPDATE_PAGE = false
  PUBLIC_WORKBOOK_OPEN_NOTIFICATION = {} as RemoteStorageItem['PUBLIC_WORKBOOK_OPEN_NOTIFICATION']
  ESSAY_WORKSHEET_OPEN_NOTIFICATION = {} as RemoteStorageItem['ESSAY_WORKSHEET_OPEN_NOTIFICATION']
  KMM_NO_ADVANCED_CURRICULUM_TOOLTIP_DISMISSED = {} as Record<string, boolean>
  MY_DB_LUCKY_WEEK_BANNER_CLOSED: number | null = null

  constructor() {
    makeAutoObservable(this)
  }

  get isReady() {
    return !!this.relationId
  }

  initRelationId = (relationId) => {
    if (!relationId) return

    this.relationId = relationId
  }

  get = async <K extends keyof RemoteStorageItem>(...keys: K[]) => {
    const { relationId } = this
    if (!relationId) {
      return
    }

    const data = await remoteStorageApi.getRemoteStorageValues(relationId, keys)

    Object.entries(data).forEach(([k, v]) => {
      this[k] = v
    })

    return data as Record<K, RemoteStorageItem[K]>
  }

  patch = async <K extends keyof RemoteStorageItem, V extends RemoteStorageItem[K]>(k: K, v: V) => {
    const { relationId } = this
    if (!relationId) {
      return
    }

    if (k in this) {
      if (typeof this[k] === 'object' && this[k] !== null) {
        this[k] = {
          ...(this[k] as Record<string, unknown>),
          ...(v as Record<string, unknown>),
        } as this[K]
      } else {
        this[k] = v as unknown as this[K]
      }

      await remoteStorageApi.updateRemoteStorageValues(relationId, { [k]: this[k] })
    }
  }

  put = async <K extends keyof RemoteStorageItem, V extends RemoteStorageItem[K]>(k: K, v: V) => {
    const { relationId } = this
    if (!relationId) {
      return
    }

    if (k in this) {
      this[k] = v as unknown as this[K]

      await remoteStorageApi.updateRemoteStorageValues(relationId, { [k]: this[k] })
    }
  }

  onLogout = async () => {
    this.patch('PREVIOUS_ACHIEVEMENT_ANALYSIS_BANNER_CLOSED_MS', null)
    this.patch('MY_DB_LUCKY_WEEK_BANNER_CLOSED', null)
  }

  migrateAchievementAnalysisCurriculumInfoData = async () => {
    const migratedData = this.PREVIOUS_SELECTED_CURRICULUM_INFO_유형분석

    if (!migratedData) {
      return
    }

    // Grade converting 로직 (number → string 변환)
    Object.entries(migratedData).forEach(([key, value]) => {
      if (key !== '__version' && typeof value === 'object' && value?.grade !== undefined) {
        if (typeof value.grade === 'number') {
          value.grade = String(value.grade) as GradeElementaryMiddle
        }
      }
    })

    const latestVersion: `v${number}` = 'v0'
    let migratedLatestVersion = {
      __version: latestVersion,
    } as AnalysisAchievementExternalV3

    // V1 마이그레이션
    if (isLessThanV2(migratedData)) {
      console.warn(
        `마이그레이션을 진행합니다. v1 -> v${AchievementAnalysisService.REMOTE_STORAGE_VERSION}`,
      )

      for (const [studentICode, value] of Object.entries(migratedData).filter(([key]) =>
        isICode(key),
      )) {
        if (typeof value === 'object' && value !== null && 'schoolType' in value) {
          migratedLatestVersion[studentICode] = {
            ...value,
            semester: value.semester || null,
            workbookFilterOptionsByCurriculumKey: {},
          }
        }
      }
    }

    // V2 마이그레이션
    if (isLessThanV3(migratedData)) {
      console.warn(
        `마이그레이션을 진행합니다. v2 -> v${AchievementAnalysisService.REMOTE_STORAGE_VERSION}`,
      )

      for (const [studentICode, value] of Object.entries(migratedData).filter(([key]) =>
        isICode(key),
      )) {
        if (typeof value === 'object' && value !== null && 'schoolType' in value) {
          migratedLatestVersion[studentICode] = {
            ...value,
            semester: value.semester || null,
            workbookFilterOptionsByCurriculumKey: {},
          }

          if (value.workbookFilterOptionsByCurriculumKey) {
            Object.entries(value.workbookFilterOptionsByCurriculumKey).forEach(
              ([curriculumKey, { workbooks }]) => {
                if (migratedLatestVersion[studentICode].workbookFilterOptionsByCurriculumKey) {
                  migratedLatestVersion[studentICode].workbookFilterOptionsByCurriculumKey![
                    curriculumKey
                  ] = {
                    workbooks: workbooks,
                  }
                }
              },
            )
          }
        }
      }
    } else if (isGreaterOrEqualThanV3(migratedData)) {
      migratedLatestVersion = migratedData
    }

    const invalidKey = Object.keys(migratedLatestVersion).find((key) => key === EMPTY_STRING_ID)

    if (invalidKey) {
      delete migratedLatestVersion[invalidKey]
    }

    const latestVersionNumber = Number(latestVersion.replace('v', ''))

    if (invalidKey || latestVersionNumber < AchievementAnalysisService.REMOTE_STORAGE_VERSION) {
      migratedLatestVersion.__version = `v${AchievementAnalysisService.REMOTE_STORAGE_VERSION}`

      // 마이그레이션 완료 후 저장
      await this.put('PREVIOUS_SELECTED_CURRICULUM_INFO_유형분석', migratedLatestVersion)
    }
  }
}

export const remoteStorageService = new RemoteStorageService()

const isICode = (key: string) => key.startsWith('I')

const isLessThanV2 = (data: AnalysisAchievementExternal): data is AnalysisAchievementExternalV1 => {
  const firstICode = Object.keys(data).find(isICode) as keyof AnalysisAchievementExternalV1
  return data[firstICode] && !('workbookFilterOptionsByCurriculumKey' in data[firstICode])
}

const isLessThanV3 = (data: AnalysisAchievementExternal): data is AnalysisAchievementExternalV2 =>
  !('__version' in data)

const isGreaterOrEqualThanV3 = (
  data: AnalysisAchievementExternal,
): data is AnalysisAchievementExternalV3 => '__version' in data
